
import Card from "@mui/material/Card";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AutocompleteAPI, Box, DataTable } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Screens from "constants/screens";
import Strings from "constants/strings";
import Constants from "constants/index";
import useDataCommon from "hooks/useDataCommon.hook";
import ProjectMemberService from "services/medkit/projectMember.service";

import { RootState } from "store";
import { DashboardLayout } from "layout";
import { IRecordProjectMember } from "commons/interfaces";
import { setListPathName } from "store/slice/titleRoute.slice";
import { setDataAlert, showLoadingDataTable } from "store/slice/common.slice";



interface IDataFilterProjectMember {
    projectId?: string;
    searchText?: string;
    pageSize?: number;
    pageNumber?: number;
    totalCount?: number;
};

const projectMemberService = new ProjectMemberService();

const ProjectMemberManagementScreen = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const { getDataProjects } = useDataCommon();

    const loadingDataTable = useSelector((state: RootState) => state.common.loadingDataTable);

    const [dataRows, setDataRows] = useState<IRecordProjectMember[]>([]);
    const [dataFilter, setDataFilter] = useState<IDataFilterProjectMember>({});

    const pageSize = searchParams.get("size");
    const pageNumber = searchParams.get("page");
    const searchText = searchParams.get("text");
    const projectId = searchParams.get("project");

    useEffect(() => {
        const size = Number(pageSize || Constants.ROW_PER_PAGE);
        const page = Number(pageNumber || Constants.PAGE_DEFAULT);


        const newReq: IDataFilterProjectMember = {
            pageSize: size,
            pageNumber: page,
            projectId: projectId,
            searchText: searchText,
        };

        loadDataProjectMember(newReq);
    }, [
        pageSize,
        pageNumber,
        searchText,
        projectId,
    ]);

    const loadDataProjectMember = async (newReq: IDataFilterProjectMember) => {
        try {
            dispatch(showLoadingDataTable(true));

            const result = await projectMemberService.getPaged({
                pageSize: newReq.pageSize,
                pageNumber: newReq.pageNumber,
                searchText: newReq.searchText,
                projectId: newReq.projectId,
            });

            setDataFilter(prev => ({ ...prev, ...newReq, totalCount: result?.totalCount || 0 }));

            setDataRows([...result?.items || []]);

        } catch (error) {
            const err = Helpers.handleExceptionError(error);
            !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
        } finally {
            dispatch(showLoadingDataTable(false));
        }
    };

    const handleFilterData = (req: IDataFilterProjectMember) => {
        let query: string = `?page=${req.pageNumber || Constants.PAGE_DEFAULT}&size=${req.pageSize || Constants.ROW_PER_PAGE}`;

        if (!Helpers.isNullOrEmpty(req.searchText)) {
            query += `&text=${req.searchText}`;
        }
        if (!Helpers.isNullOrEmpty(req.projectId)) {
            query += `&project=${req.projectId}`;
        }

        navigate(Screens.PROJECT_MEMBER + query, { replace: true });

        dispatch(setListPathName({ pathname: Screens.PROJECT_MEMBER, query }));
    };

    const handleDelete = (id: string) => {
        Helpers.showConfirmAlert(
            Strings.PROJECT_MEMBER_MANAGEMENT.CONFIRM_DELETE_PROJECT_MEMBER,
            async () => {
                try {
                    dispatch(showLoadingDataTable(true));

                    await projectMemberService.delete(id);

                    await loadDataProjectMember({ ...dataFilter });

                    dispatch(setDataAlert({ message: Strings.PROJECT_MEMBER_MANAGEMENT.DELETE_PROJECT_MEMBER_SUCCESS, type: "success" }));
                } catch (error) {
                    const err = Helpers.handleExceptionError(error);
                    !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
                } finally {
                    dispatch(showLoadingDataTable(false));
                }
            },
            () => { },
        );
    };

    const dataColumns = useMemo(() => ([
        { Header: Strings.PROJECT_MANAGEMENT.PROJECT_NAME, accessor: "projectName" },
        { Header: Strings.PROJECT_MEMBER_MANAGEMENT.MEMBER_NAME, accessor: "userName" },
    ]), []);

    return (
        <DashboardLayout
            isPermission
            allowSettingLayout
            requiredOrganization
            title={Strings.PROJECT_MEMBER_MANAGEMENT.TITLE}
            route={[{ title: Strings.PROJECT_MEMBER_MANAGEMENT.TITLE, route: "" }]}
            listActions={[
                {
                    title: Strings.COMMON.ADD_NEW,
                    onPress: () => { navigate(Screens.PROJECT_MEMBER_CREATE) }
                }
            ]}
        >
            <Card sx={{ padding: 2 }}>
                <DataTable
                    isShowIndex
                    loading={loadingDataTable}
                    totalCount={dataFilter?.totalCount || 0}
                    rowPerPage={dataFilter?.pageSize || Constants.ROW_PER_PAGE}
                    pageNumber={dataFilter?.pageNumber || Constants.PAGE_DEFAULT}
                    onChangePageSize={(value) => {
                        handleFilterData({ ...dataFilter, pageSize: value, });
                    }}
                    onChangePageNumber={(value) => {
                        handleFilterData({ ...dataFilter, pageNumber: value, });
                    }}
                    table={{ columns: dataColumns as any, rows: dataRows }}

                    searchText={dataFilter?.searchText}
                    placeholderSearch={Strings.PROJECT_MEMBER_MANAGEMENT.PLACEHOLDER_SEARCH_TEXT}
                    onSearch={(value) => {
                        handleFilterData({
                            ...dataFilter,
                            searchText: value,
                            pageNumber: Constants.PAGE_DEFAULT,
                        });
                    }}
                    onSearchChange={(value) => {
                        setDataFilter(prev => ({
                            ...prev,
                            searchText: value,
                            pageNumber: Constants.PAGE_DEFAULT,
                        }));
                    }}
                    actionList={(row) => ([
                        {
                            key: "delete",
                            iconName: "delete",
                            title: Strings.COMMON.DELETE,
                            callback: (row) => {
                                handleDelete(row.id);
                            },
                        },
                    ])}
                    leftElement={
                        <Box width={"240px"}>
                            <AutocompleteAPI
                                defaultValue={dataFilter.projectId}
                                label={Strings.PROJECT_MANAGEMENT.PROJECT}
                                placeholder={Strings.PROJECT_MANAGEMENT.SELECT_PROJECT}
                                getApi={getDataProjects}
                                requestGetApi={{}}
                                onChange={(value) => { }}
                                onChangeItem={(value) => {
                                    handleFilterData({
                                        ...dataFilter,
                                        projectId: value?.code as string,
                                        pageNumber: Constants.PAGE_DEFAULT,
                                    });
                                }}
                            />
                        </Box>
                    }
                />
            </Card>
        </DashboardLayout>
    );
};

export default ProjectMemberManagementScreen;
